import IMAGES from '@/assets/images';
import CustomImage from '@/components/CustomImage';
import { XIcon } from '@/icons';
import { addZero, errorToast, formatTo12Hour } from '@/utils';
import { Button, Empty, Flex, Skeleton } from 'antd';
import { useEffect, useState } from 'react';
import {
  useAssignMultipleShiftMultipleUserMutation,
  useDeleteShiftMutation,
  useGetAllShiftsQuery,
} from '../../../../../../app/features/employee/emp-attendance/emp-attendance.api';
import { useGetAllEmployeesQuery } from '../../../../../../app/features/employee/emp-dashboard/emp-dashboard.api';
import ConfirmationModal from '../../../../../../components/ConfirmationModal';
import useAuth from '../../../../../../hooks/useAuth';
import useError from '../../../../../../hooks/useError';
import useModal from '../../../../../../hooks/useModal';
import { toast } from '../../../../../../lib/toast';
import SelectWithInfo from './SelectWithInfo';

export default function ShiftList() {
  const { tenantId, organizationId } = useAuth();
  const { open, handleOpen, handleClose } = useModal<string, 'CONFIRM'>();
  const { isLoading, isFetching, error, data } = useGetAllShiftsQuery({
    params: {
      skip: 0,
      limit: 0,
      tenantId,
      platform: 'web',
    },
  });
  useError(error, 'Failed to fetch shift data');

  const { data: employeesData } = useGetAllEmployeesQuery({
    params: {
      skip: 0,
      limit: 0,
      tenant_id: tenantId,
    },
  });

  const [assignMultipleShifts, { isLoading: isAssigning }] =
    useAssignMultipleShiftMultipleUserMutation();
  const [deleteShift] = useDeleteShiftMutation();
  const [deletingId, setDeletingId] = useState<string | null>(null);

  // State to store all users per shift (including existing + new)
  const [shiftAssignments, setShiftAssignments] = useState<
    Record<string, string[]>
  >({});
  const [initialAssignments, setInitialAssignments] = useState<
    Record<string, string[]>
  >({});

  const loading = isLoading || isFetching;
  const shiftList = data?.data?.data;
  const shiftMeta = data?.data?.meta;

  const employees =
    employeesData?.data?.data?.map((emp) => ({
      id: emp.user_id,
      name: emp.name,
      empId: emp.employee_id,
      role: emp.designation?.name,
      avatar: emp.photo || IMAGES.avatar,
    })) || [];

  // Initialize assignments from existing data
  useEffect(() => {
    if (shiftList) {
      const initial: Record<string, string[]> = {};
      shiftList.forEach((shift) => {
        if (shift.users?.length) {
          initial[shift.id] = shift.users.map((user) => user.userId);
        }
      });
      setInitialAssignments(initial);
      setShiftAssignments(initial);
    }
  }, [shiftList]);

  const handleDeleteShift = async (id: string) => {
    try {
      setDeletingId(id);
      await deleteShift({ id }).unwrap();
      // await refetch();
      toast('success', 'Shift deleted successfully');
    } catch (err) {
      errorToast(err, 'Failed to delete shift. Please try again');
    } finally {
      setDeletingId(null);
      handleClose();
    }
  };

  const handleSelectUser = (shiftId: string, userId: string | undefined) => {
    if (!userId) return;

    setShiftAssignments((prev) => {
      const currentUsers = prev[shiftId] || [];

      return {
        ...prev,
        [shiftId]: [...currentUsers, userId],
      };
    });
  };

  const handleRemoveUser = (shiftId: string, userId: string) => {
    setShiftAssignments((prev) => {
      const currentUsers = prev[shiftId] || [];
      const updatedUsers = currentUsers.filter((id) => id !== userId);

      if (updatedUsers.length === 0) {
        const updated = { ...prev };
        delete updated[shiftId];
        return updated;
      }

      return {
        ...prev,
        [shiftId]: updatedUsers,
      };
    });
  };

  const handleSubmitAssignments = async () => {
    // Check if there are any changes
    const hasChanges = Object.keys(shiftAssignments).some((shiftId) => {
      const current = shiftAssignments[shiftId] || [];
      const initial = initialAssignments[shiftId] || [];

      return (
        current.length !== initial.length ||
        !current.every((id) => initial.includes(id))
      );
    });

    // Also check for removed shifts
    const hasRemovedShifts = Object.keys(initialAssignments).some(
      (shiftId) =>
        !shiftAssignments[shiftId] || shiftAssignments[shiftId].length === 0,
    );

    if (!hasChanges && !hasRemovedShifts) {
      toast('warning', 'No changes to submit');
      return;
    }

    try {
      // Transform state to API format - only include shifts with users
      const assignments = shiftList?.map((shift) => ({
        tenantId,
        organizationId,
        shiftId: shift.id,
        userIds: shiftAssignments[shift.id] || [],
      }));

      await assignMultipleShifts({ assignments }).unwrap();
      toast('success', 'Shifts updated successfully');

      // Refetch to get updated data
      // await refetch();
    } catch (err) {
      errorToast(err, 'Failed to assign shifts');
    }
  };

  const getUsersForShift = (shiftId: string) => {
    const assignedUserIds = shiftAssignments[shiftId] || [];
    return assignedUserIds.map((userId) => {
      const employee = employees.find((emp) => emp.id === userId);
      const existingUser = shiftList
        ?.find((s) => s.id === shiftId)
        ?.users?.find((u) => u.userId === userId);

      return {
        userId,
        userName: employee?.name || existingUser?.userName || 'Unknown',
        isNew: !initialAssignments[shiftId]?.includes(userId),
        photo: employee?.avatar || IMAGES.avatar,
      };
    });
  };

  const getTotalChanges = () => {
    let addedCount = 0;
    let removedCount = 0;

    Object.keys({ ...shiftAssignments, ...initialAssignments }).forEach(
      (shiftId) => {
        const current = shiftAssignments[shiftId] || [];
        const initial = initialAssignments[shiftId] || [];

        current.forEach((id) => {
          if (!initial.includes(id)) addedCount++;
        });

        initial.forEach((id) => {
          if (!current.includes(id)) removedCount++;
        });
      },
    );

    return { addedCount, removedCount };
  };

  // Get all assigned user IDs across all shifts
  const getAllAssignedUserIds = () => {
    const allUserIds = new Set<string>();
    Object.values(shiftAssignments).forEach((userIds) => {
      userIds.forEach((id) => allUserIds.add(id));
    });
    return Array.from(allUserIds);
  };

  const { addedCount, removedCount } = getTotalChanges();
  const hasChanges = addedCount > 0 || removedCount > 0;
  const allAssignedUserIds = getAllAssignedUserIds();

  return (
    <div>
      <h6 className="dark:text-white text-lg font-bold mb-4">
        Shift List ({loading ? '...' : addZero(shiftMeta?.total)})
      </h6>

      {loading ? (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="p-4 rounded-xl border dark:border-theme-gray-700 space-y-4"
            >
              <Flex justify="space-between" wrap>
                <Skeleton.Input active size="small" className="!w-32" />
                <Flex align="center" gap={16}>
                  <Skeleton.Button active size="small" className="!w-28" />
                  <Skeleton.Button active size="small" className="!w-8 mr-4" />
                </Flex>
              </Flex>
              <Skeleton.Input active size="small" className="!w-48" />
            </div>
          ))}
        </div>
      ) : shiftList?.length ? (
        <>
          <div className="space-y-4 max-h-[300px] overflow-y-auto pr-2 custom-scroll">
            {shiftList.map((el) => {
              const users = getUsersForShift(el?.id);

              return (
                <div
                  key={el?.id}
                  className="p-4 rounded-xl border dark:border-theme-gray-700 dark:text-white space-y-4"
                >
                  <Flex justify="space-between" wrap>
                    <h6 className="font-bold">{el?.name}</h6>
                    <Flex align="center" gap={16}>
                      <SelectWithInfo
                        data={employees.filter(
                          (emp) => !allAssignedUserIds.includes(emp.id),
                        )}
                        onChange={(userId) =>
                          handleSelectUser(el?.id, userId as string)
                        }
                        disabled={isAssigning}
                      />

                      <Button
                        size="small"
                        variant="outlined"
                        className="!bg-transparent w-8 dark:border-theme-gray-700 dark:text-white"
                        onClick={() =>
                          handleOpen({
                            type: 'CONFIRM',
                            data: el?.id,
                          })
                        }
                        loading={deletingId === el?.id}
                        disabled={deletingId !== null || isAssigning}
                        icon={<XIcon />}
                      />
                    </Flex>
                  </Flex>

                  <h6>
                    {formatTo12Hour(el?.startTime)} -{' '}
                    {formatTo12Hour(el?.endTime)}
                  </h6>

                  {users.length > 0 && (
                    <Flex align="center" gap={12} wrap>
                      {users.map((user) => (
                        <Flex
                          align="center"
                          className={`p-1 rounded-lg space-x-2.5 bg-theme-gray-700`}
                          key={user?.userId}
                        >
                          <CustomImage
                            width={20}
                            height={20}
                            src={user?.photo}
                            alt={user.userName}
                            className="rounded-full object-cover"
                          />
                          <h6 className="text-sm">{user.userName}</h6>
                          <button
                            className="!bg-theme-slate-900 rounded border-none hover:bg-red-500 transition-colors cursor-pointer"
                            onClick={() =>
                              handleRemoveUser(el?.id, user.userId)
                            }
                            disabled={isAssigning}
                          >
                            <XIcon />
                          </button>
                        </Flex>
                      ))}
                    </Flex>
                  )}
                </div>
              );
            })}
          </div>

          <Flex justify="end" gap="small" className="mt-6">
            <Button
              type="primary"
              size="large"
              onClick={handleSubmitAssignments}
              loading={isAssigning}
              disabled={!hasChanges}
              className="!text-neutral-700 !text-base !font-semibold"
            >
              Done
            </Button>
          </Flex>
        </>
      ) : (
        <div className="text-center py-8">
          <Empty />
        </div>
      )}

      {/* Confirmation Modal */}
      <ConfirmationModal
        data={open.data}
        type={open.type}
        handleClose={handleClose}
        handleSubmit={() => {
          if (open?.data) {
            handleDeleteShift(open.data);
          }
        }}
        open={open.open && open.type === 'CONFIRM'}
        loading={deletingId === open.data}
        title="Are you sure?"
        subTitle="You want to delete this shift?"
        buttonName="Delete"
      />
    </div>
  );
}
